import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import './AdminPages.css';

const ManageCareers = () => {
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCareer, setEditingCareer] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: 'Full-time',
    experience: '',
    description: '',
    requirements: [],
    responsibilities: []
  });
  const [reqInput, setReqInput] = useState('');
  const [respInput, setRespInput] = useState('');

  useEffect(() => {
    fetchCareers();
  }, []);

  const fetchCareers = async () => {
    try {
      const response = await axios.get('/api/careers');
      setCareers(response.data.data);
    } catch (error) {
      console.error('Error fetching careers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCareer) {
        await axios.put(`/api/careers/${editingCareer._id}`, formData);
      } else {
        await axios.post('/api/careers', formData);
      }
      fetchCareers();
      closeModal();
    } catch (error) {
      console.error('Error saving career:', error);
      alert('Error saving job posting. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this job posting?')) {
      try {
        await axios.delete(`/api/careers/${id}`);
        fetchCareers();
      } catch (error) {
        console.error('Error deleting career:', error);
      }
    }
  };

  const addRequirement = () => {
    if (reqInput.trim()) {
      setFormData({ ...formData, requirements: [...formData.requirements, reqInput.trim()] });
      setReqInput('');
    }
  };

  const removeRequirement = (index) => {
    setFormData({
      ...formData,
      requirements: formData.requirements.filter((_, i) => i !== index)
    });
  };

  const addResponsibility = () => {
    if (respInput.trim()) {
      setFormData({ ...formData, responsibilities: [...formData.responsibilities, respInput.trim()] });
      setRespInput('');
    }
  };

  const removeResponsibility = (index) => {
    setFormData({
      ...formData,
      responsibilities: formData.responsibilities.filter((_, i) => i !== index)
    });
  };

  const openModal = (career = null) => {
    if (career) {
      setEditingCareer(career);
      setFormData({
        title: career.title,
        department: career.department,
        location: career.location,
        type: career.type,
        experience: career.experience,
        description: career.description,
        requirements: career.requirements || [],
        responsibilities: career.responsibilities || []
      });
    } else {
      setEditingCareer(null);
      setFormData({
        title: '',
        department: '',
        location: '',
        type: 'Full-time',
        experience: '',
        description: '',
        requirements: [],
        responsibilities: []
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCareer(null);
    setReqInput('');
    setRespInput('');
  };

  if (loading) {
    return (
      <AdminLayout title="Manage Careers">
        <div className="loading-state">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Manage Careers">
      <div className="admin-page">
        <div className="page-actions">
          <button className="btn-add" onClick={() => openModal()}>
            <FaPlus /> Add Job Opening
          </button>
        </div>

        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Department</th>
                <th>Location</th>
                <th>Type</th>
                <th>Experience</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {careers.map((career) => (
                <tr key={career._id}>
                  <td className="font-semibold">{career.title}</td>
                  <td>{career.department}</td>
                  <td>{career.location}</td>
                  <td>
                    <span className="type-badge">{career.type}</span>
                  </td>
                  <td>{career.experience}</td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-edit" onClick={() => openModal(career)}>
                        <FaEdit />
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(career._id)}>
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {careers.length === 0 && (
            <div className="no-data">No job openings found. Add your first job posting!</div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editingCareer ? 'Edit Job Opening' : 'Add Job Opening'}
            </h2>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Job Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Department *</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Location *</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    placeholder="e.g., Mumbai, Remote"
                  />
                </div>

                <div className="form-group">
                  <label>Job Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Experience Required *</label>
                <input
                  type="text"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  required
                  placeholder="e.g., 3-5 years"
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Requirements</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={reqInput}
                    onChange={(e) => setReqInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addRequirement())}
                    placeholder="Add a requirement"
                  />
                  <button type="button" onClick={addRequirement} className="btn-add-item">
                    Add
                  </button>
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {formData.requirements.map((req, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: 'var(--light-gray)', borderRadius: '4px' }}>
                      <span>{req}</span>
                      <button type="button" onClick={() => removeRequirement(index)} style={{ color: '#dc2626', background: 'none', padding: '0 0.5rem' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Responsibilities</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={respInput}
                    onChange={(e) => setRespInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addResponsibility())}
                    placeholder="Add a responsibility"
                  />
                  <button type="button" onClick={addResponsibility} className="btn-add-item">
                    Add
                  </button>
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {formData.responsibilities.map((resp, index) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', backgroundColor: 'var(--light-gray)', borderRadius: '4px' }}>
                      <span>{resp}</span>
                      <button type="button" onClick={() => removeResponsibility(index)} style={{ color: '#dc2626', background: 'none', padding: '0 0.5rem' }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingCareer ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ManageCareers;
